import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, User, Calendar, Clock, FileText, Bike, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { evaluationTests, getInterpretation, calculateTotalScore } from '@/lib/evaluationUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import TestCard from '@/components/evaluation/TestCard';
import { useSupabaseData } from '@/contexts/SupabaseDataContext';

const PsychomotorEvaluation = ({ onNavigate, student }) => {
  const { toast } = useToast();
  const { students, addEvaluation } = useSupabaseData();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    instructor: '',
    student_id: student ? student.id : '',
    student_name: student ? student.name : '',
    responsible_name: student ? student.responsible_name : '',
    polo: student ? student.polo : '',
    duration: '',
    rides_bike: '',
    scores: {
      dynamicBalance: 0,
      staticBalance: 0,
      motorCoordination: 0,
      bilateralIntegration: 0,
      spatialPerception: 0,
      bodySchema: 0,
      forceControl: 0
    }
  });

  useEffect(() => {
    if (student) {
      setFormData(prev => ({ 
        ...prev, 
        student_id: student.id,
        student_name: student.name,
        responsible_name: student.responsible_name,
        polo: student.polo
      }));
    }
  }, [student]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleScoreChange = (testId, score) => {
    setFormData(prev => ({
      ...prev,
      scores: { ...prev.scores, [testId]: score }
    }));
  };

  const getInterpretationText = (score) => {
    if (score >= 0 && score <= 7) return "Intervenções específicas podem ser necessárias para fortalecer as habilidades.";
    if (score >= 8 && score <= 14) return "O indivíduo demonstra habilidades, mas ainda há espaço para melhorias.";
    if (score >= 15 && score <= 21) return "Boa coordenação e controle nas habilidades avaliadas.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.instructor || !formData.student_id || !formData.duration || formData.rides_bike === '') {
      toast({
        title: "Erro de Validação",
        description: "Por favor, preencha todos os campos obrigatórios (*).",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }
    
    setLoading(true);

    const total_score = calculateTotalScore(formData.scores);
    const result_interpretation = getInterpretation(total_score);
    const global_interpretation = getInterpretationText(total_score);

    const newEvaluationData = {
        student_id: formData.student_id,
        date: formData.date,
        instructor: formData.instructor,
        duration: formData.duration,
        rides_bike: formData.rides_bike,
        scores: formData.scores,
        total_score,
        result_interpretation,
        global_interpretation
    };

    const newEvaluation = await addEvaluation(newEvaluationData);
    setLoading(false);

    if (newEvaluation) {
      const selectedStudent = students.find(s => s.id.toString() === formData.student_id.toString());
      onNavigate('studentDashboard', selectedStudent);
    }
  };

  const totalScore = calculateTotalScore(formData.scores);
  const interpretation = getInterpretation(totalScore);

  if (!student) {
     return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center">
        <p className="text-white text-xl mb-4">Selecione um aluno para fazer a avaliação.</p>
        <Button onClick={() => onNavigate('dashboard')} className="ml-4">Voltar para o Dashboard</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            onClick={() => onNavigate('studentDashboard', student)}
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para {student.name}
          </Button>
          
          <h1 className="text-4xl font-bold text-white mb-2">
            Avaliação Psicomotora
          </h1>
          <p className="text-xl text-purple-300">
            Formulário de Avaliação Psicomotora – Pedalando pela Inclusão
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <Card className="bg-transparent border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                Dados da Aula
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-white flex items-center">
                    <Calendar className="mr-1 h-4 w-4" />
                    Data *
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instructor" className="text-white">Instrutor *</Label>
                  <Input
                    id="instructor"
                    type="text"
                    value={formData.instructor}
                    onChange={(e) => handleInputChange('instructor', e.target.value)}
                    placeholder="Nome do instrutor"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="student" className="text-white flex items-center">
                    <User className="mr-1 h-4 w-4" />
                    Aluno *
                  </Label>
                  <Select
                    id="student"
                    value={formData.student_id}
                    onChange={(e) => handleInputChange('student_id', e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                    disabled={true}
                  >
                    <option value="" className="bg-slate-800 text-white">
                      {formData.student_name ? `${formData.student_name} - ${formData.polo}` : 'Selecione o aluno'}
                    </option>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration" className="text-white flex items-center">
                    <Clock className="mr-1 h-4 w-4" />
                    Duração da Aula *
                  </Label>
                  <Input
                    id="duration"
                    type="text"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    placeholder="Ex: 60 minutos"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="rides_bike" className="text-white flex items-center">
                    <Bike className="mr-1 h-4 w-4" />
                    Aluno pedala? *
                  </Label>
                  <Select
                    id="rides_bike"
                    value={formData.rides_bike}
                    onChange={(e) => handleInputChange('rides_bike', e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                  >
                    <option value="" className="bg-slate-800 text-white">Selecione uma opção</option>
                    <option value="sim" className="bg-slate-800 text-white">Sim</option>
                    <option value="nao" className="bg-slate-800 text-white">Não</option>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            {evaluationTests.map((test, index) => (
              <TestCard
                key={test.id}
                test={test}
                index={index}
                score={formData.scores[test.id]}
                handleScoreChange={handleScoreChange}
              />
            ))}
          </div>

          <div className="space-y-6">
            <Card className="bg-transparent border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Quadro de Pontuação por Habilidade</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-white">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="text-left py-2 font-semibold">Habilidade Psicomotora</th>
                        <th className="text-center py-2 font-semibold">0</th>
                        <th className="text-center py-2 font-semibold">1</th>
                        <th className="text-center py-2 font-semibold">2</th>
                        <th className="text-center py-2 font-semibold">3</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evaluationTests.map((test, index) => (
                        <tr key={test.id} className="border-b border-white/10">
                          <td className="py-2">{index + 1}. {test.name}</td>
                          {[0, 1, 2, 3].map(scoreValue => (
                            <td key={scoreValue} className="text-center py-2">
                              <div className={`w-4 h-4 rounded-full border border-white/50 mx-auto ${
                                formData.scores[test.id] === scoreValue ? 'bg-green-500' : ''
                              }`} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-green-500/10 border-green-500/20">
                <CardHeader>
                  <CardTitle className="text-white">Pontuação Total</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-green-300 mb-2">
                    {totalScore}/21
                  </div>
                  <p className="text-green-300/80">
                    Soma dos pontos obtidos em todas as habilidades
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-blue-500/10 border-blue-500/20">
                <CardHeader>
                  <CardTitle className="text-white">Interpretação</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-lg font-semibold text-blue-300 mb-2">
                    {interpretation}
                  </div>
                  <p className="text-blue-300/80 text-sm">
                    {getInterpretationText(totalScore)}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => onNavigate('studentDashboard', student)}
              className="border-white/20 text-white hover:bg-white/10"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
              disabled={loading}
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {loading ? 'Salvando...' : 'Salvar Avaliação'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PsychomotorEvaluation;